import { axiosInstance } from './axios';
import { 
    ICustomerAccountingEntry, 
    ICustomerAccountBalance, 
    ICustomerAccountSummary,
    IVendorAccountingEntry,
    IVendorAccountBalance,
    IVendorAccountSummary
} from '../interfaces/accounting';

// API methods for accessing backend services
export const api = {
    // Currency-related methods
    getCurrencies: async () => {
        const response = await axiosInstance.get('/currency');
        return response.data;
    },

    // Inventory-related methods
    getStock: async () => {
        const response = await axiosInstance.get('/inventory');
        return response.data;
    },

    getStockAvailable: async () => {
        const response = await axiosInstance.get('/inventory/available');
        return response.data;
    },

    // Tax-related methods
    getTaxes: async () => {
        const response = await axiosInstance.get('/taxes');
        return response.data;
    },

    // Company-related methods
    uploadCompanyLogo: async (companyId: number, file: File) => {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axiosInstance.post(`/company/logo/${companyId}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },

    // Customer Accounting methods
    getCustomerAccountingEntries: async (
        customerId: number, 
        fromDate?: string, 
        toDate?: string
    ): Promise<ICustomerAccountingEntry[]> => {
        const params = new URLSearchParams();
        if (fromDate) params.append('fromDate', fromDate);
        if (toDate) params.append('toDate', toDate);
        
        const response = await axiosInstance.get(`/customers/${customerId}/accounting/entries?${params}`);
        return response.data;
    },

    getCustomerAccountBalance: async (
        customerId: number, 
        asOfDate?: string
    ): Promise<ICustomerAccountBalance> => {
        const params = new URLSearchParams();
        if (asOfDate) params.append('asOfDate', asOfDate);
        
        const response = await axiosInstance.get(`/customers/${customerId}/accounting/balance?${params}`);
        return response.data;
    },

    getCustomerAccountSummary: async (customerId: number): Promise<ICustomerAccountSummary> => {
        const response = await axiosInstance.get(`/customers/${customerId}/accounting/summary`);
        return response.data;
    },

    downloadCustomerStatement: async (
        customerId: number,
        fromDate?: string,
        toDate?: string
    ): Promise<void> => {
        const params = new URLSearchParams();
        if (fromDate) params.append('fromDate', fromDate);
        if (toDate) params.append('toDate', toDate);

        const response = await axiosInstance.get(`/customers/${customerId}/statement?${params}`, {
            responseType: 'blob',
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `customer_statement_${customerId}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    },

    // Vendor Accounting methods
    getVendorAccountingEntries: async (
        vendorId: number, 
        fromDate?: string, 
        toDate?: string
    ): Promise<IVendorAccountingEntry[]> => {
        const params = new URLSearchParams();
        if (fromDate) params.append('fromDate', fromDate);
        if (toDate) params.append('toDate', toDate);
        
        const response = await axiosInstance.get(`/vendors/${vendorId}/accounting/entries?${params}`);
        return response.data;
    },

    getVendorAccountBalance: async (
        vendorId: number, 
        asOfDate?: string
    ): Promise<IVendorAccountBalance> => {
        const params = new URLSearchParams();
        if (asOfDate) params.append('asOfDate', asOfDate);
        
        const response = await axiosInstance.get(`/vendors/${vendorId}/accounting/balance?${params}`);
        return response.data;
    },

    getVendorAccountSummary: async (vendorId: number): Promise<IVendorAccountSummary> => {
        const response = await axiosInstance.get(`/vendors/${vendorId}/accounting/summary`);
        return response.data;
    },

    // Add other API methods as needed here
};

export default api; 